import {BrandedFooter} from '@components/branding';
import {
  ChecklistProvider,
  ChecklistTarget,
  useChecklist
} from '@components/checklist';
import CompoundTranslation from '@components/compound-translation';
import {ConditionalError} from '@components/conditional-error';
import {FieldCheckBox} from '@components/field-builder/field-checkbox';
import I9DocumentTitle from '@components/i9-document-title';
import {RequiredWrapper} from '@components/required';
import {
  DocumentImage,
  DocumentImageGroup
} from '@containers/scalable-document-list/document-image';
import {FieldType} from '@models/field-builder/field-type';
import {DocumentLoadingStatus} from '@screens/review-section2/document-review/document-loading-status';
import InteractableDocument from '@screens/review-section2/document-review/interactable-document';
import {ReviewErrors} from '@screens/review-section2/document-review/review-errors';
import {
  getDocumentReviewTranslationLists,
  getDocumentReviewTranslations,
  GlobalTranslation
} from '@screens/review-section2/document-review/review-translations';
import {
  ButtonSubtitle,
  DocumentReviewList,
  Instruction
} from '@screens/review-section2/document-review/styles';
import {
  Buttons,
  DashedDivider
} from '@screens/review-section2/styles';
import {
  CenterContainer,
  Content,
  Header
} from '@screens/review-section3/styles';
import {createTranslationService} from '@services/factories';
import {StringFormatService} from '@services/StringFormatService';
import {authenticationParamsContext} from '@stores/authentication/authentication-params-context';
import {IDocumentReviewStore} from '@stores/onboarding-review/document-review-store';
import {
  PageContainer,
  ScrollContainer
} from '@styles/container-styles';
import {
  ActionRowButton,
  ConfirmRowButton,
  RejectRowButton
} from '@styles/shared-styles';
import {VerticalLayout} from '@styles/spacing-styles';
import {cacheTranslations} from '@utils/cache-translations';
import { I9DocumentInfoDto } from '@dtos/I9Form';
import {observer} from 'mobx-react';
import * as React from 'react';
import * as Scroll from 'react-scroll';

interface Props {
  store: IDocumentReviewStore;
  onConfirm: () => void;
  onResubmit: () => void;
  onDecline: () => void;
}

const DocumentCheckbox: React.FunctionComponent<{
  store: IDocumentReviewStore;
  documentGroup: DocumentImageGroup;
}> = observer(
  ({
     store,
     documentGroup
   }) => {
    const translations = getDocumentReviewTranslations();

    const documentDetail: I9DocumentInfoDto | undefined = store.documentTypesValidationsAll.find(
                                                (
                                                  item: I9DocumentInfoDto
                                                ) =>
                                                  item.documentType ===
                                                  documentGroup.type
                                              );
    const renderRequiredDocumentLabel = (documentTitle: string) => (
      <CompoundTranslation template={translations.documentConfirmCheckboxTemplate}
                           data={[
                             <RequiredWrapper key={0}>{documentTitle}</RequiredWrapper>
                           ]}/>
    );

    const isConfirmed = store.confirmedDocuments[documentGroup.type];
    return (
      <ChecklistTarget name={`confirm-document-${documentGroup.type}`}
                       valid={isConfirmed}>
        <FieldCheckBox type={FieldType.CHECKBOX}
                       id={documentGroup.type}
                       label={(
                         <I9DocumentTitle fallbackName={documentGroup.type}
                                          documentType={documentGroup.type}
                                          documentTitle={documentDetail?.title ?? ''}
                                          renderTitle={renderRequiredDocumentLabel}/>
                       )}
                       helperText={(
                         <ConditionalError condition={!isConfirmed && store.needsDocumentsConfirmed}>
                           {translations.confirmCorrectDocuments}
                         </ConditionalError>
                       )}
                       setValueMethod={(confirmed) => store.setDocumentConfirmation(documentGroup.type, confirmed)}
                       value={isConfirmed}/>
      </ChecklistTarget>
    );
  }
);

const scrollContainerId = 'document-review-scroll-container';

const DocumentLegibilityCheck: React.FunctionComponent<{ store: IDocumentReviewStore }> = observer(props => {
  const translations = getDocumentReviewTranslations();
  return (
    <ChecklistTarget name={'documents-legibility'}
                     valid={props.store.confirmedAllDocumentsLegible}>
      <FieldCheckBox type={FieldType.CHECKBOX}
                     id={'onboarding-review-page-all-confirmed'}
                     disabled={!props.store.areDocumentsLoaded}
                     label={(
                       <RequiredWrapper>{translations.confirmAllLegible}</RequiredWrapper>
                     )}
                     helperText={(
                       <ConditionalError
                         condition={!props.store.confirmedAllDocumentsLegible && props.store.needsLegibilityConfirmed}>
                         {translations.confirmLegibleDocumentImages}
                       </ConditionalError>
                     )}
                     setValueMethod={confirmed => props.store.setAllDocumentsLegible(confirmed)}
                     value={props.store.confirmedAllDocumentsLegible}/>
    </ChecklistTarget>
  );
});

const I9DocumentReviewPage: React.FunctionComponent<Props> = observer((props) => {
  const {
    store,
    onConfirm,
    onDecline,
    onResubmit
  } = props;

  const translations = getDocumentReviewTranslations();
  const translationLists = getDocumentReviewTranslationLists();

  const globalTranslations = cacheTranslations(
    createTranslationService(),
    GlobalTranslation
  );

  const checklistContext = useChecklist();

  const renderDocumentConfirmation = (documentGroup: DocumentImageGroup) => {
    return (
      <DocumentCheckbox store={store}
                        documentGroup={documentGroup}/>
    );
  };

  const renderInteractableDocument = (document: DocumentImage) => {
    return (
      <InteractableDocument document={document}
                            store={store}/>
    );
  };

  const scrollToFirstFailedField = () => {
    const firstInvalidField = checklistContext.getFirstInvalidTarget();
    if (!firstInvalidField) {
      return;
    }

    const scrollProps = {
      smooth: true,
      containerId: scrollContainerId
    };
    Scroll.scroller.scrollTo(firstInvalidField, scrollProps);
  };

  const handleConfirmation = () => {
    store.validate();
    if (!store.allConfirmed) {
      scrollToFirstFailedField();
      return;
    }

    onConfirm();
  };

  const handleResubmission = () => onResubmit();
  const handleDecline = () => onDecline();

  return (
    <ScrollContainer id={scrollContainerId}>
      <ChecklistProvider value={checklistContext}>
        <CenterContainer fillAvailable={true}>
          <PageContainer>
            <Header>
              {
                translationLists.optionalInstructions.map((line, index) =>
                  <div key={index}>{line}</div>
                )
              }
              <Instruction>
                <authenticationParamsContext.Consumer>
                  {
                    params => params && StringFormatService.format(translations.instructionsTemplate, [params!.fullName])
                  }
                </authenticationParamsContext.Consumer>
              </Instruction>
            </Header>
            <Content>
              <DocumentLoadingStatus store={store}
                                     translations={translations}/>
              <DocumentReviewList documents={store.documents}
                                  renderRowContent={renderDocumentConfirmation}
                                  renderDocument={renderInteractableDocument}/>
              <VerticalLayout bottomSpacing='medium'>
                {
                  store.documents.length ? (
                    <DocumentLegibilityCheck store={store}/>
                  ) : null
                }
              </VerticalLayout>
              <VerticalLayout innerSpacing='small'
                              bottomSpacing='none'>
                <Buttons justifyContent='center'
                         topSpacing='none'
                         bottomSpacing='none'>
                  <ActionRowButton onClick={handleResubmission}>
                    {translations.resubmitButton}
                  </ActionRowButton>
                </Buttons>
                <ButtonSubtitle>
                  {store.enabledUploadDocuments ?
                    translations.resubmitSubtitleWhenReUpload :
                    translations.resubmitSubtitle
                  }
                </ButtonSubtitle>
                <ReviewErrors store={store}
                              globalTranslations={globalTranslations}/>
              </VerticalLayout>
              <DashedDivider/>
              <Buttons innerSpacing='small'>
                <RejectRowButton onClick={handleDecline}>
                  {translations.declineButton}
                </RejectRowButton>
                <ConfirmRowButton onClick={handleConfirmation}>
                  {translations.confirmButton}
                </ConfirmRowButton>
              </Buttons>
            </Content>
          </PageContainer>
        </CenterContainer>
        <BrandedFooter/>
      </ChecklistProvider>
    </ScrollContainer>
  );
});

export default I9DocumentReviewPage;



// WEBPACK FOOTER //
// ./src/screens/review-section2/document-review/review-page.tsx